import React, { useState } from 'react';
import { styleDefinitions as styles } from '../../theme/styles';
import { X, Send } from 'lucide-react';
import { WorkItem, Comment } from '../../types';
import { v4 as uuidv4 } from 'uuid';

interface CommentModalProps {
  item: WorkItem;
  onSave: (item: WorkItem) => void;
  onClose: () => void;
}

export const CommentModal: React.FC<CommentModalProps> = ({ item, onSave, onClose }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [author, setAuthor] = useState(localStorage.getItem('commentAuthor') || 'User');

  // Handle ESC key press
  React.useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleAddComment = () => {
    if (newComment.trim() && author.trim()) {
      const comment: Comment = {
        id: uuidv4(),
        text: newComment.trim(),
        author: author.trim(),
        timestamp: new Date()};

      localStorage.setItem('commentAuthor', author);

      onSave({
        ...item,
        comments: [...item.comments, comment],
        metadata: {
          ...item.metadata,
          modified: new Date(),
          version: item.metadata.version + 1}});

      setNewComment('');
    }
  };

  const handleDeleteComment = (commentId: string) => {
    onSave({
      ...item,
      comments: item.comments.filter(c => c.id !== commentId),
      metadata: {
        ...item.metadata,
        modified: new Date(),
        version: item.metadata.version + 1}});
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'});
  };

  return (
    <div style={{...styles.position.fixed, zIndex: 1000 }} onClick={onClose}>
      <div style={{...styles.spacing.py4, 
    ...styles.size.minHScreen,
    ...styles.spacing.px4,
    ...styles.layout.flex,
     alignItems: "center" ,
     justifyContent: "center" 
  }}>
        <div style={{...styles.shadows.xl, 
    ...styles.background.white,
    ...styles.utility.roundedLg,
     maxWidth: "42rem" ,
    ...styles.size.wFull,
    ...styles.spacing.paddingXl,
    ...styles.layout.flex,
    ...styles.layout.flexColumn
  }} onClick={(e) => e.stopPropagation()}>
          <div style={{
    ...styles.layout.flex,
     justifyContent: "space-between" ,
     alignItems: "center" ,
    ...styles.spacing.marginBottomLg
  }}>
          <div>
            <h2 style={{
    ...styles.typography.textXl,
    ...styles.typography.fontSemibold
  }}>Comments</h2>
            <p style={{
    ...styles.typography.textSm,
     color: "var(--color-gray-600)" 
  }}>{item.fileName}</p>
          </div>
          <button
            onClick={onClose}
            style={{
    ...styles.spacing.paddingXs ,
    ...styles.utility.rounded
  }}
          >
            <X size={20} />
          </button>
          </div>

          <div style={{...styles.utility.overflowAuto, 
     flex: 1 ,
    ...styles.spacing.marginBottomLg,
     gap: "var(--space-3)" 
  }}>
          {item.comments.length === 0 ? (
            <p style={{...styles.spacing.py4, 
     color: "var(--color-gray-500)" ,
    ...styles.typography.textCenter
  }}>No comments yet</p>
          ) : (
            item.comments.map(comment => (
              <div key={comment.id} style={{
    ...styles.background.gray50,
    ...styles.utility.roundedLg,
    ...styles.spacing.paddingMd
  }}>
                <div style={{
    ...styles.layout.flex,
     justifyContent: "space-between" ,
     alignItems: "flex-start" ,
    ...styles.spacing.marginBottomSm
  }}>
                  <div>
                    <span style={{
    ...styles.typography.fontMedium,
    ...styles.typography.textSm
  }}>{comment.author}</span>
                    <span style={{
    ...styles.typography.textXs,
     color: "var(--color-gray-500)" ,
    ...styles.spacing.marginLeftSm
  }}>
                      {formatDate(comment.timestamp)}
                    </span>
                  </div>
                  <button
                    onClick={() => handleDeleteComment(comment.id)}
                    style={{...styles.colors.error, ...styles.typography.textSm}}
                  >
                    Delete
                  </button>
                </div>
                <p style={{...styles.typography.textSm}}>{comment.text}</p>
              </div>
            ))
          )}
        </div>

        <div style={{...styles.extended.pt4, ...styles.utility.borderTop}}>
          <div style={{
    ...styles.layout.flex,
    ...styles.spacing.gap2,
    ...styles.spacing.marginBottomSm
  }}>
            <input
              type="text"
              value={author}
              onChange={e => setAuthor(e.target.value)}
              style={{...styles.utility.border, 
    ...styles.spacing.px3,
    ...styles.spacing.py2,
    ...styles.utility.border,
    ...styles.utility.rounded
  }}
              placeholder="Your name"
            />
          </div>
          <div style={{
    ...styles.layout.flex,
    ...styles.spacing.gap2
  }}>
            <input
              type="text"
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleAddComment()}
              style={{...styles.utility.border, 
     flex: 1 ,
    ...styles.spacing.px3,
    ...styles.spacing.py2,
    ...styles.utility.border,
    ...styles.utility.rounded
  }}
              placeholder="Add a comment..."
            />
            <button
              onClick={handleAddComment}
              onMouseEnter={() => setIsHovered(true)} 
              onMouseLeave={() => setIsHovered(false)}
              style={{
                ...(isHovered ? styles.states.hover.gray50 : {}),
                ...styles.spacing.px4,
                ...styles.spacing.py2,
                ...styles.typography.textWhite,
    ...styles.utility.rounded,
    ...styles.layout.flex,
     alignItems: "center" ,
    ...styles.spacing.gap2
  }}
            >
              <Send size={16} />
              Send
            </button>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};